import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BreeceService } from 'src/app/services/breece.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-breece-sync-log',
  templateUrl: './breece-sync-log.component.html',
  styleUrls: ['./breece-sync-log.component.scss']
})
export class BreeceSyncLogComponent implements OnInit, OnDestroy {

  @Input() storeId: string = '';

  logs: any[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalItems: number = 0;
  pageSize: number = 20;

  // Detail modal
  showLogDetails: boolean = false;
  selectedLog: any = null;
  loadingDetail: boolean = false;

  // Auto-refresh when a sync is running
  private refreshTimer: any = null;

  Math = Math;

  constructor(
    private breeceService: BreeceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadLogs();
    }
  }

  ngOnDestroy(): void {
    this.clearRefreshTimer();
  }

  loadLogs(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.breeceService.getSyncLogs(this.storeId, this.currentPage, this.pageSize).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? null;
        if (data) {
          this.logs = data.logs || [];
          const pagination = data.pagination || {};
          this.totalItems = pagination.totalItems ?? 0;
          this.totalPages = pagination.totalPages ?? 1;
          this.currentPage = pagination.currentPage ?? 1;
        } else {
          this.logs = [];
          console.warn('Unexpected Breece sync logs response:', res);
        }
        this.isLoading = false;
        this.scheduleRefreshIfNeeded();
      },
      (error: any) => {
        console.error('Failed to load Breece sync logs:', error);
        this.errorMessage = error?.error?.response?.message || 'Failed to load sync logs';
        this.toastr.error(this.errorMessage);
        this.isLoading = false;
      }
    );
  }

  private scheduleRefreshIfNeeded(): void {
    const hasRunning = this.logs.some(l => l.status === 'running');
    if (hasRunning && !this.refreshTimer) {
      this.refreshTimer = setInterval(() => this.loadLogs(), 30000);
    } else if (!hasRunning) {
      this.clearRefreshTimer();
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLogs();
  }

  viewLogDetails(log: any): void {
    // Show list-item data immediately (stats are available) then replace with full detail
    // which includes the complete errors array — always fetch, never rely on list payload
    this.selectedLog = log;
    this.showLogDetails = true;
    this.loadingDetail = true;

    this.breeceService.getSyncLogDetail(this.storeId, log._id).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        this.selectedLog = data.log || data;
        this.loadingDetail = false;
      },
      (error: any) => {
        this.loadingDetail = false;
        const msg = error?.error?.response?.message || 'Failed to load sync run detail';
        this.toastr.error(msg);
      }
    );
  }

  closeLogDetails(): void {
    this.showLogDetails = false;
    this.selectedLog = null;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'success': return 'bg-success';
      case 'running': return 'bg-warning text-dark';
      case 'partial': return 'bg-warning text-dark';
      case 'failed':  return 'bg-danger';
      default:        return 'bg-secondary';
    }
  }

  getDuration(log: any): string {
    if (!log.startedAt || !log.completedAt) {
      return '—';
    }
    const ms = new Date(log.completedAt).getTime() - new Date(log.startedAt).getTime();
    if (ms < 1000) {
      return `${ms}ms`;
    }
    if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  }

  trackByLogId(index: number, log: any): string {
    return log._id || String(index);
  }
}
